import { useState } from "react";
import { Button, Card, Input, Tag } from "antd";
import type { Gap } from "../types";
import { BUILDING_BLOCK_LABELS } from "../schema";
import { Check, Loader2, Mic, MicOff, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useMicRecorder, RecordingObjectType } from "@vojomo-core/shared";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

interface GapsPanelProps {
  gaps: Gap[];
  sessionId: string;
  onGapsUpdated: (gaps: Gap[]) => void;
  drafts: Record<string, string>;
  onDraftChange: (id: string, value: string) => void;
  onSubmitGap: (id: string, value: string) => Promise<void>;
}

function groupByBlock(gaps: Gap[]): Record<string, Gap[]> {
  const grouped: Record<string, Gap[]> = {};
  for (const gap of gaps) {
    const key = gap.blockKey || "general";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(gap);
  }
  return grouped;
}

export function GapsPanel({
  gaps,
  sessionId,
  onGapsUpdated,
  drafts,
  onDraftChange,
  onSubmitGap,
}: GapsPanelProps) {
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [recordingForId, setRecordingForId] = useState<string | null>(null);
  const { isRecording, startRecording, stopRecording, isInitializing, isProcessingAudio } = useMicRecorder();

  const handleDismiss = async (gapId: string) => {
    setDismissingId(gapId);
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/gaps/${gapId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        onGapsUpdated(data.gaps);
        onDraftChange(gapId, "");
      }
    } catch (error) {
      console.error("Failed to dismiss gap:", error);
    } finally {
      setDismissingId(null);
    }
  };

  const openGaps = gaps.filter((g) => g.status === "open");
  const filledGaps = gaps.filter((g) => g.status === "filled");

  const grouped = groupByBlock(gaps);

  return (
    <div>
      <div className="px-3 pt-3 pb-2 bg-white border-b border-slate-100 shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-base font-semibold leading-snug">Gaps</div>
            <p className="text-xs text-muted-foreground">
              Fill in information gaps below to help Vo refine your roadmap.
            </p>
          </div>
          {gaps.length > 0 && (
            <div className="flex items-center gap-1 shrink-0">
              <Tag className="text-xs" data-testid="badge-gaps-count">{openGaps.length} open</Tag>
              {filledGaps.length > 0 && (
                <Tag className="text-xs" data-testid="badge-gaps-filled-count">{filledGaps.length} filled</Tag>
              )}
            </div>
          )}
        </div>
      </div>

      {gaps.length === 0 ? (
        <div className="px-4 pt-4">
          <div className="flex items-center justify-center py-8 border border-dashed rounded-md bg-muted/20">
            <p className="text-xs text-muted-foreground" data-testid="text-no-gaps">
              No gaps identified yet. As Vo analyzes your roadmap, information gaps will appear here.
            </p>
          </div>
        </div>
      ) : (
      <div className="px-4 pt-3 pb-4 space-y-4">
        {Object.entries(grouped).map(([blockKey, blockGaps]) => {
          const blockLabel =
            blockKey === "general" ? "General" : (BUILDING_BLOCK_LABELS[blockKey]?.label || blockKey);
          return (
            <div key={blockKey} className="space-y-2">
              <div className="flex items-center gap-2">
                <Tag className="text-[10px] py-0" data-testid={`badge-block-${blockKey}`}>
                  {blockLabel}
                </Tag>
                <span className="text-[10px] text-muted-foreground">
                  {blockGaps.filter((g) => g.status === "open").length} open
                </span>
              </div>

              <AnimatePresence mode="popLayout">
                {blockGaps.map((gap) => {
                  const isFilled = gap.status === "filled";
                  const currentResponse = drafts[gap.id] || "";

                  return (
                    <motion.div
                      key={gap.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                      data-testid={`gap-${gap.id}`}
                    >
                      <Card
                        styles={{ body: { padding: 12 } }}
                        className={`transition-colors ${
                          isFilled
                            ? "border-emerald-300 bg-emerald-50/50"
                            : currentResponse.trim()
                              ? "border-blue-300 bg-blue-50/50"
                              : ""
                        }`}
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm" data-testid={`text-gap-description-${gap.id}`}>
                              {gap.description}
                            </p>
                            {!isFilled && (
                              <Button
                                type="text"
                                size="small"
                                style={{ padding: "0 4px", height: 22, flexShrink: 0 }}
                                className="text-muted-foreground hover:text-red-500"
                                onClick={() => handleDismiss(gap.id)}
                                disabled={dismissingId === gap.id}
                                title="Dismiss gap"
                                data-testid={`button-dismiss-gap-${gap.id}`}
                              >
                                {dismissingId === gap.id
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <X className="h-3 w-3" />}
                              </Button>
                            )}
                          </div>

                          {isFilled && gap.userResponse ? (
                            <div className="ml-5 pl-1">
                              <p className="text-sm text-emerald-600" data-testid={`text-gap-response-${gap.id}`}>
                                {gap.userResponse}
                              </p>
                            </div>
                          ) : !isFilled ? (
                            <div className="ml-5 pl-1 flex items-center gap-1">
                              <Input
                                size="small"
                                placeholder="Provide the missing information..."
                                value={currentResponse}
                                onChange={(e) => onDraftChange(gap.id, e.target.value)}
                                onPressEnter={async () => {
                                  if (!currentResponse.trim()) return;
                                  setSubmittingId(gap.id);
                                  await onSubmitGap(gap.id, currentResponse);
                                  setSubmittingId(null);
                                }}
                                data-testid={`input-gap-${gap.id}`}
                              />
                              <Button
                                type="text"
                                size="small"
                                style={{ padding: "0 4px", height: 22, flexShrink: 0 }}
                                className={
                                  isRecording && recordingForId === gap.id
                                    ? "text-red-500 hover:text-red-600"
                                    : "text-muted-foreground hover:text-foreground"
                                }
                                disabled={isInitializing || isProcessingAudio || (isRecording && recordingForId !== gap.id)}
                                onClick={() => {
                                  if (isRecording && recordingForId === gap.id) {
                                    stopRecording();
                                    setRecordingForId(null);
                                  } else {
                                    setRecordingForId(gap.id);
                                    startRecording(RecordingObjectType.MODUS_CHAT, async (transcript) => {
                                      onDraftChange(gap.id, transcript);
                                      setRecordingForId(null);
                                    });
                                  }
                                }}
                                title={isRecording && recordingForId === gap.id ? "Stop recording" : "Voice input"}
                              >
                                {isProcessingAudio && recordingForId === gap.id
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : isRecording && recordingForId === gap.id
                                  ? <MicOff className="h-3 w-3" />
                                  : <Mic className="h-3 w-3" />}
                              </Button>
                              <Button
                                type="text"
                                size="small"
                                style={{ padding: "0 4px", height: 22, flexShrink: 0 }}
                                className="text-emerald-600 hover:text-emerald-700"
                                disabled={!currentResponse.trim() || submittingId === gap.id}
                                onClick={async () => {
                                  setSubmittingId(gap.id);
                                  await onSubmitGap(gap.id, currentResponse);
                                  setSubmittingId(null);
                                }}
                                title="Submit answer"
                                data-testid={`button-submit-gap-${gap.id}`}
                              >
                                {submittingId === gap.id
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <Check className="h-3 w-3" />}
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
